import { useEffect, useState } from 'react';
import { Header } from '../../../shared/components/Header';
import { Text } from '@gs-ux-uitoolkit-react/text';
import { Button } from '@gs-ux-uitoolkit-react/button';
import { Footer } from '../../../shared/components/Footer';
import { FormControl } from '@gs-ux-uitoolkit-react/form';
import { FormInput } from '../../../shared/components/FormInput';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import { toastService } from '@gs-ux-uitoolkit-react/toast';
import { Checkbox } from "@gs-ux-uitoolkit-react/checkbox";
import { LegacyIcon } from "@gs-ux-uitoolkit-react/icon-font";

export function CreatePasswordAndSecurity() {
    const navigate = useNavigate(); // Initialize the navigate function
    const { repId } = useParams<{ repId: string }>(); // Extract repId from the URL
    const [isPasswordCreated, setIsPasswordCreated] = useState(false);
    const [firmRepInfo, setFirmRepInfo] = useState<any>();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [check, setCheck] = useState(false);
    const [passwordMasked, setPasswordMasked] = useState(true);
    const [confirmPasswordMasked, setConfirmPasswordMasked] = useState(true);
    const [isSetPassword, setIsSetPassword] = useState(false);
    const passwordsMatch = password === confirmPassword; // Check if passwords match
    const [linkDeactivatedError, setLinkDeactivatedError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (repId) {
                const url = `/api/v2/advisors-invitations/${repId}/firm-approved-rep`;
                try {
                    const response = await axios.get(url);
                    setFirmRepInfo(response.data);
                    if (response.data.invitationstatus === 'password&secretQuestionAnswerCreated') {
                        setIsPasswordCreated(true);
                    }
                } catch (error: any) {
                    if (error?.response?.status === 401) {
                        navigate(`/advisor-invitations/mfa-attempts/${repId}`);
                    } else if (error?.response?.data?.displayMessage) {
                        setLinkDeactivatedError(error.response.data.displayMessage);
                    }
                }
            }
        };

        fetchData();
    }, [repId, navigate]);

    const validatePassword = (password: string): string | null => {
        if (password.length < 10 || password.length > 64) {
            return "Password must be between 10-64 characters long.";
        }
        if (!/[A-Z]/.test(password)) {
            return "Password must contain at least one upper-case letter.";
        }
        if (!/[a-z]/.test(password)) {
            return "Password must contain at least one lower-case letter.";
        }
        if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return "Password must contain at least one number or symbol.";
        }
        return null; // No errors
    };

    const handleSubmit = () => {
        const errors: string[] = [];

        const passwordError = validatePassword(password);
        if (passwordError) {
            errors.push(passwordError);
        }

        if (!password || !confirmPassword || !passwordsMatch || !check) {
            errors.push('Please fill in all fields!');
        }

        if (errors.length > 0) {
            errors.forEach((error, index) => {
                setTimeout(() => {
                    toastService.show({
                        status: 'error',
                        message: error,
                        placement: 'top',
                        dismissible: true,
                        className: 'top-20',
                        autoDismiss: 3_500
                    });
                }, index * 100); // Slight delay between each toast
            });
            return;
        }
        const url = `/api/v2/advisors-invitations/${repId}/set-password-and-secret-question-answer`;
        const advisorRequest = {
            setPasswordRequest: {
                newPassword: password
            },
            updateCustomerAgreementRequest: {
                isMemberAgreement: check ? 'Y' : 'N'
            }
        };

        axios.post(url, advisorRequest)
            .then(async (response) => {
                console.log(response);
                setIsSetPassword(true);
                setIsPasswordCreated(true);
            })
            .catch((error) => {
                console.log(error);
                toastService.show({
                    status: 'error',
                    message: `Failed to create Password!`,
                    placement: 'top',
                    dismissible: true,
                    className: 'top-20',
                    autoDismiss: 3_500
                });
            });
    };

    return (
        <>
            <Header />
            {linkDeactivatedError === "Your account is locked due to too many failed attempts. Please contact support." ? (
                <div className="bg-red-10 p-4 rounded w-full max-w-screen-md mx-auto mt-4">
                    <Text typography="textBody02Medium" color="red060" className="mb-2">
                        Error
                    </Text>
                    <Text typography="textBody02Regular" color="red060">
                        {linkDeactivatedError}
                    </Text>
                </div>
            ) : (
                <>
                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 py-6 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                            <LegacyIcon name="check-circle" type={"filled"} size="lg" className="text-green-60" />
                            <Text typography="textSubtitle02Medium">
                                Congratulations {firmRepInfo?.firstname || ""}!
                            </Text>
                        </div>
                    </div>
                    {isPasswordCreated ? (
                        <div className="flex-1 flex flex-col px-16 min-h-[400px]">
                            <Text typography="textBody01Regular" className='px-6'>
                                Your password has been created. Please proceed to{" "}
                                <a
                                    onClick={() => navigate('/')}
                                    style={{ cursor: 'pointer', color: 'blue' }}
                                >
                                    Sign In
                                </a>.
                            </Text>
                        </div>
                    ) : (
                        <>
                            {!isSetPassword && (
                                <>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                            <Text>Your user has been created. Please set a password to enable online access to the portal.</Text>
                                        </div>
                                    </div>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 py-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                            <Text typography="textSubtitle03Medium">Create a password</Text>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-8 max-w-[1200px] w-full px-16">
                                        <div className="flex flex-col gap-6 py-6">
                                            <FormInput
                                                isRequired
                                                className="w-full"
                                                placeholder='enter password'
                                                label='Password'
                                                name={`password`}
                                                type={passwordMasked ? "password" : "text"}
                                                setValue={(newVal) => setPassword(newVal)}
                                                value={password}
                                                trailingContent={
                                                    <div
                                                        className="pointer-events-auto"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setPasswordMasked(!passwordMasked)}
                                                    >
                                                        <LegacyIcon
                                                            name={passwordMasked ? "visibility" : "visibility-off"}
                                                            type="filled"
                                                        />
                                                    </div>
                                                }
                                            />
                                            <FormInput
                                                isRequired
                                                className="w-full"
                                                placeholder='enter password'
                                                label='Confirm Password'
                                                name={`confirmPassword`}
                                                type={confirmPasswordMasked ? "password" : "text"}
                                                setValue={(newVal) => {
                                                    setConfirmPassword(newVal);
                                                }}
                                                value={confirmPassword}
                                                error={confirmPassword && !passwordsMatch ? "Passwords do not match" : undefined}
                                                trailingContent={
                                                    <div
                                                        className="pointer-events-auto"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setConfirmPasswordMasked(!confirmPasswordMasked)}
                                                    >
                                                        <LegacyIcon
                                                            name={confirmPasswordMasked ? "visibility" : "visibility-off"}
                                                            type="filled"
                                                        />
                                                    </div>
                                                }
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <Text>Your password must:</Text>
                                            <ul className="list-disc list-inside">
                                                <li><Text>Be between 10-64 characters long</Text></li>
                                                <li><Text>Contain one upper-case letter</Text></li>
                                                <li><Text>Contain one lower-case letter</Text></li>
                                                <li><Text>Contain one number or symbol</Text></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="flex px-16 w-full">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                            <Checkbox checked={check} onChange={() => setCheck(!check)}>
                                                <Text typography="textBody01Regular">I have read, understand and agree to the terms and the conditions set forth in the <a href={`${import.meta.env.BASE_URL}/customerAgreement.pdf`} target="_blank" className='text-blue-60'>Customer Agreement</a>.</Text>
                                            </Checkbox>
                                        </div>
                                    </div>
                                    <div className="col-span-12 flex justify-end mt-4 px-6 py-4 bg-gray-10">
                                        <Button
                                            className="bg-blue-60 rounded"
                                            size="lg"
                                            type="submit"
                                            onClick={handleSubmit}
                                            disabled={!password || !confirmPassword || !passwordsMatch || !check} 
                                            appearance="neutral" // Disable button if checkbox is not checked
                                        >
                                            Save Password & Proceed to Login
                                        </Button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            <Footer />
        </>
    );
}
